import React from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './context/AuthContext'
import { SHOW_STOCK } from './lib/features'
import AppShell from './components/AppShell'
import { Spinner } from './components/ui'
import LoginPage from './pages/LoginPage'
import DashboardPage from './pages/DashboardPage'
import ProductsPage from './pages/ProductsPage'
import ProductEditorPage from './pages/ProductEditorPage'
import InventoryPage from './pages/InventoryPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailPage from './pages/OrderDetailPage'
import AnalyticsPage from './pages/AnalyticsPage'
import CategoriesPage from './pages/CategoriesPage'
import ActivityPage from './pages/ActivityPage'
import CustomersPage from './pages/CustomersPage'
import InvoicesPage from './pages/InvoicesPage'
import InvoiceEditorPage from './pages/InvoiceEditorPage'
import InvoicePrintPage from './pages/InvoicePrintPage'
import SuppliersPage from './pages/SuppliersPage'
import TeamPage from './pages/TeamPage'
import SettingsPage from './pages/SettingsPage'

/**
 * Gate every route behind authorisation.
 *
 * `isAuthorised` is signed-in AND allow-listed, not just signed-in — see
 * AuthContext for why those are different questions. RLS enforces the same thing
 * server-side; this only decides what to render.
 *
 * `bare` drops the sidebar and top bar. Used only by the printable invoice,
 * which has to be alone on the page — panel chrome hidden with a print
 * stylesheet has a habit of reappearing on page two of a long document.
 */
const Protected = ({ children, bare = false }) => {
  const { isAuthorised, loading, inviteForSomeoneElse } = useAuth()
  if (loading) return <Spinner label="Checking your access…" />
  if (!isAuthorised || inviteForSomeoneElse) return <LoginPage />
  return bare ? children : <AppShell>{children}</AppShell>
}

const App = () => (
  <AuthProvider>
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Protected><DashboardPage /></Protected>} />
        <Route path="/products" element={<Protected><ProductsPage /></Protected>} />
        <Route path="/products/new" element={<Protected><ProductEditorPage /></Protected>} />
        <Route path="/products/:id" element={<Protected><ProductEditorPage /></Protected>} />
        {/* Inventory is hidden while SHOW_STOCK is off (see lib/features.js).
            Kept as a route rather than removed so a bookmarked link lands on the
            catalogue instead of bouncing off the catch-all. */}
        <Route
          path="/inventory"
          element={SHOW_STOCK ? <Protected><InventoryPage /></Protected> : <Navigate to="/products" replace />}
        />
        <Route path="/orders" element={<Protected><OrdersPage /></Protected>} />
        <Route path="/orders/:id" element={<Protected><OrderDetailPage /></Protected>} />
        <Route path="/analytics" element={<Protected><AnalyticsPage /></Protected>} />
        <Route path="/categories" element={<Protected><CategoriesPage /></Protected>} />
        <Route path="/activity" element={<Protected><ActivityPage /></Protected>} />
        <Route path="/customers" element={<Protected><CustomersPage /></Protected>} />

        {/* The print route is declared before /invoices/:id so ":id" can never
            swallow it — a document numbered "print" is impossible, but relying
            on that rather than on route order is how this breaks later. */}
        <Route path="/invoices" element={<Protected><InvoicesPage /></Protected>} />
        <Route path="/invoices/new" element={<Protected><InvoiceEditorPage /></Protected>} />
        <Route path="/invoices/:id/print" element={<Protected bare><InvoicePrintPage /></Protected>} />
        <Route path="/invoices/:id" element={<Protected><InvoiceEditorPage /></Protected>} />
        <Route path="/suppliers" element={<Protected><SuppliersPage /></Protected>} />
        <Route path="/team" element={<Protected><TeamPage /></Protected>} />
        <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </AuthProvider>
)

export default App
